import { useEffect, useRef, useState } from 'react';
import { createIdentitySession, getIdentityStatus } from '@/lib/api';
import { getStripe } from '@/lib/stripe';

export type IdentityStatus =
  | 'unverified'
  | 'requires_input'
  | 'processing'
  | 'verified'
  | 'canceled';

export function IdentityStep(props: {
  authToken: string;
  status: IdentityStatus;
  setStatus: (s: IdentityStatus) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Tracks whether the user has clicked "Verify" *in this mount*. Without this
  // we'd treat a `processing` status returned by the on-mount status fetch as
  // "active processing" — the user lands on the page, sees a disabled
  // PROCESSING… button, and has no idea why. With it, a stale `processing`
  // (left over from an earlier attempt that Stripe never resolved) becomes a
  // recoverable state with a clear "Restart verification" button.
  const [activeThisSession, setActiveThisSession] = useState(false);
  // setStatus is read inside the polling effect — keep a stable ref so we don't
  // need it in the dep array (and don't restart the loop on every parent render).
  const setStatusRef = useRef(props.setStatus);
  setStatusRef.current = props.setStatus;

  // Refresh status when this step is mounted (in case the user just returned
  // from the modal in another tab / their previous attempt finished while
  // they were away).
  useEffect(() => {
    let cancelled = false;
    getIdentityStatus(props.authToken)
      .then((r) => {
        if (!cancelled) props.setStatus(r.status);
      })
      .catch(() => {
        /* ignore */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.authToken]);

  // Background polling while the backend reports `processing`. Stripe's webhook
  // can take longer than the in-modal poll loop covers — without this, the UI
  // can sit stuck on the spinner indefinitely. Polls every 2s; stops as soon
  // as status moves to a terminal state (verified / canceled / requires_input).
  useEffect(() => {
    if (props.status !== 'processing') return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const r = await getIdentityStatus(props.authToken);
        if (cancelled) return;
        if (r.status !== props.status) setStatusRef.current(r.status);
      } catch {
        /* ignore — keep polling */
      }
    };
    const id = window.setInterval(tick, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [props.authToken, props.status]);

  async function startVerify() {
    setBusy(true);
    setError(null);
    setActiveThisSession(true);
    try {
      const { clientSecret } = await createIdentitySession(props.authToken);
      const stripe = await getStripe();
      if (!stripe) {
        setError("Couldn't load Stripe. Please refresh and try again.");
        return;
      }
      // verifyIdentity opens a Stripe-hosted modal; resolves on close.
      type StripeWithIdentity = NonNullable<Awaited<ReturnType<typeof getStripe>>> & {
        verifyIdentity?: (cs: string) => Promise<{ error?: { message?: string } }>;
      };
      const fn = (stripe as StripeWithIdentity).verifyIdentity;
      if (!fn) {
        setError('Stripe Identity is unavailable on this Stripe build.');
        return;
      }
      const result = await fn(clientSecret);
      if (result?.error) {
        setError(result.error.message ?? 'Verification was canceled.');
        return;
      }
      // Refresh once on modal close. If the webhook beat us here, the user
      // sees `verified` immediately; otherwise we surface `processing` and
      // the background poll above keeps trying every 2s.
      const r = await getIdentityStatus(props.authToken);
      props.setStatus(r.status === 'unverified' ? 'processing' : r.status);
    } catch (e) {
      setError(`Couldn't start verification: ${String((e as Error).message ?? e)}`);
    } finally {
      setBusy(false);
    }
  }

  if (props.status === 'verified') {
    return <p className="text-sm text-emerald-400">ID verified. Continue to the final step.</p>;
  }

  const lead = (
    <p className="text-sm text-bone/70">
      We use Stripe Identity to capture a government ID and selfie. The image is stored securely
      with Stripe — only George and admin can review it later.
    </p>
  );

  // Stale `processing` (from a previous attempt that never resolved). The
  // user hasn't acted in this session, so don't lock them into the spinner —
  // explain what's happening and give them a way to start over. The
  // background poll above is still ticking in case the prior attempt
  // resolves on its own.
  if (props.status === 'processing' && !activeThisSession) {
    return (
      <div className="grid gap-3" data-testid="identity-stale-processing">
        {lead}
        <div className="rounded-md border-2 border-yellow-700 bg-yellow-200 p-3 text-sm text-yellow-950">
          <p className="font-medium">Previous verification still in progress.</p>
          <p className="mt-1">
            Stripe is still processing an earlier attempt. You can wait a few seconds, or restart
            the verification below.
          </p>
        </div>
        <button
          type="button"
          onClick={startVerify}
          disabled={busy}
          className="btn-primary inline-flex w-full items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          data-testid="identity-restart-button"
        >
          {busy && <Spinner />}
          {busy ? 'Opening verification…' : 'Restart verification'}
        </button>
        {error && (
          <div
            role="alert"
            className="rounded border border-accent/50 bg-accent/10 p-3 text-sm text-accent"
          >
            {error}
          </div>
        )}
      </div>
    );
  }

  const processing = props.status === 'processing';
  const buttonDisabled = busy || processing;
  const buttonLabel = busy
    ? 'Opening verification…'
    : processing
      ? 'Processing…'
      : props.status === 'requires_input'
        ? 'Resume ID verification'
        : 'Verify my ID';

  return (
    <div className="grid gap-3">
      {lead}
      <button
        type="button"
        onClick={startVerify}
        disabled={buttonDisabled}
        aria-busy={buttonDisabled}
        className="btn-primary inline-flex w-full items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        data-testid="identity-verify-button"
      >
        {buttonDisabled && <Spinner />}
        {buttonLabel}
      </button>
      {error && (
        <div
          role="alert"
          className="rounded border border-accent/50 bg-accent/10 p-3 text-sm text-accent"
        >
          {error}
        </div>
      )}
      {processing && (
        <p
          className="inline-flex items-center gap-2 text-xs text-bone/60"
          data-testid="identity-processing"
          aria-live="polite"
        >
          <Spinner />
          Your ID is being processed. This usually completes in a few seconds.
        </p>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
      />
    </svg>
  );
}
